import { ErrorInfo } from '../../../interfaces/error-info.interface';
import React from 'react';

/**
 * Shown when a list could not be loaded, instead of the empty state.
 *
 * An empty state and a failed load look identical to a user — "0 teams" is
 * what a 403 looked like when the lead platform's permissions had never been
 * granted, which made a blocked screen read as a working, empty one. If the
 * data did not arrive, say so.
 */
export const LoadError: React.FC<{ message?: string; onRetry?: () => void }> = ({
  message,
  onRetry
}) => (
  <div className="flex flex-col items-center gap-2 rounded-2xl border border-red-100 bg-red-50/50 py-10">
    <p className="font-gilroy-medium text-neutral-500">{message || 'Could not load this list'}</p>
    {onRetry ? (
      <button type="button" className="text-sm text-primary-500" onClick={onRetry}>
        Try again
      </button>
    ) : null}
  </div>
);

/**
 * A message for a failed load, with 403 called out by name.
 *
 * "Something went wrong" is not actionable; "you lack a permission" tells the
 * reader to go and ask for it, which is the only thing that fixes it.
 */
export function describeLoadError(error: ErrorInfo): string {
  if (error.status === 403 || error.errorCode === 'Forbidden') {
    return 'You do not have permission to see this. Ask an admin to grant it.';
  }
  if (error.status === 401) return 'Your session has expired — sign in again.';
  return error.errorMessage || 'Could not load this list';
}
